<template>
  <div class="exports-index">
    <p class="text-muted">
      NRCan full and billing exports still run on the nightly and monthly schedules.
      Use the buttons below to queue any report on demand (processing runs in the background).
    </p>

    <div class="btn-toolbar" style="margin-bottom: 15px;">
      <div
        v-for="opt in exportTypes"
        :key="opt.type"
        class="btn-group"
        style="margin-right: 8px; margin-bottom: 8px;"
      >
        <button
          type="button"
          class="btn btn-primary"
          :disabled="queueing === opt.type"
          @click="queueExport(opt.type)"
        >
          <span v-if="queueing === opt.type" class="glyphicon glyphicon-refresh spin" aria-hidden="true"></span>
          {{ opt.label }}
        </button>
      </div>
    </div>

    <hr>

    <table class="table table-striped">
      <thead>
        <tr>
          <th>Date</th>
          <th style="text-align:center">Type</th>
          <th style="text-align:center">Status</th>
          <th>Filename</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in exports" :key="row.id">
          <td>{{ formatDate(row.created_at) }}</td>
          <td style="text-align:center">{{ row.friendly_type }}</td>
          <td style="text-align:center">
            <span v-if="isProcessing(row)" class="glyphicon glyphicon-refresh spin" style="margin-right: 6px;" aria-hidden="true"></span>
            <label :class="'label ' + labelClass(row.aasm_state)">{{ row.aasm_state }}</label>
          </td>
          <td>
            <a v-if="row.download_path" :href="row.download_path">{{ row.filename }}</a>
            <span v-else-if="row.processing_error" :title="row.processing_error" class="text-danger">
              {{ row.filename || '—' }}
            </span>
            <span v-else>{{ row.filename || '—' }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import csrfMixin from '../mixins/csrfMixin'

const EXPORT_TYPES = [
  { type: 'Export::NRCan::Full', label: 'NRCan Evaluations' },
  { type: 'Export::NRCan::Billing', label: 'NRCan Billing' },
  { type: 'Export::NRCan::Compare', label: 'NRCan Compare' },
  { type: 'Export::RightSizing::Billing', label: 'Right-Sizing Billing' }
]

export default {
  mixins: [csrfMixin],
  data () {
    return {
      exports: [],
      exportTypes: EXPORT_TYPES,
      queueing: null,
      pollTimer: null
    }
  },
  mounted () {
    const el = document.getElementById('exports-index-props')
    if (el && el.textContent) {
      try {
        this.exports = JSON.parse(el.textContent.trim())
      } catch (e) {
        this.exports = []
      }
    }
    this.schedulePoll()
  },
  beforeDestroy () {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
    }
  },
  methods: {
    labelClass (state) {
      const map = {
        queued: 'label-default',
        processing: 'label-warning',
        available: 'label-success',
        failed: 'label-danger'
      }
      return map[state] || 'label-default'
    },
    isProcessing (row) {
      return row.aasm_state === 'queued' || row.aasm_state === 'processing'
    },
    formatDate (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      const opts = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
      return d.toLocaleString(undefined, opts)
    },
    schedulePoll () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
      }
      this.pollTimer = setInterval(() => {
        if (this.exports.some(e => this.isProcessing(e))) {
          this.loadExports()
        }
      }, 2500)
    },
    loadExports () {
      fetch('/exports.json', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(r => r.json())
        .then(data => {
          if (data.exports) {
            this.exports = data.exports
          }
        })
        .catch(() => {})
    },
    queueExport (type) {
      this.queueing = type
      fetch('/exports', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ export: { type } })
      })
        .then(r => {
          if (!r.ok) {
            return r.json().then(j => { throw new Error(j.error || r.statusText) })
          }
          return r.json()
        })
        .then(data => {
          if (data.export) {
            this.exports = [data.export, ...this.exports]
          } else {
            this.loadExports()
          }
        })
        .catch(() => {
          window.alert('Could not queue export.')
        })
        .finally(() => {
          this.queueing = null
        })
    }
  }
}
</script>

<style scoped>
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
</style>
